'use client';

/**
 * @fileoverview `SearchSheet` (WEB-220) — modal overlay organism that hosts
 * the existing `SearchAutocomplete` organism for global search.
 *
 * The sheet is triggered by the search button in `AppHeader` (WEB-218) and
 * shares open/close state through the `useSearchSheet` hook. On result
 * selection it routes to the per-city discover surface with the chosen
 * value as the `q=` param and closes itself.
 *
 * Layout:
 *   - Desktop (≥720px): top-anchored card that slides down from the top.
 *   - Mobile  (≤720px): full-viewport sheet that dismisses the bottom-nav.
 *
 * a11y:
 *   - Radix `Dialog` provides focus trap, escape-to-close, and focus
 *     restoration to the triggering button.
 *   - `DialogTitle` is rendered visually-hidden (`sr-only`) so screen
 *     readers announce the dialog without cluttering the visual layout.
 *   - The text input owns programmatic focus on open via `autoFocus` —
 *     the surrounding Radix focus trap keeps focus inside while the sheet
 *     is open.
 *
 * Behavior contracts (from WEB-220 acceptance criteria):
 *   - Closes on Escape, backdrop click, and result selection.
 *   - Reuses `SearchAutocomplete` — no duplicate query logic.
 *   - On result selection: navigates via `router.push()` + closes.
 */

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { Search as SearchIcon } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';

import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/primitives/dialog';
import { cn } from '@/components/atoms/primitives/utils';
import { SearchAutocomplete } from '@/components/organisms/SearchAutocomplete';
import { useSearchSheet } from '@/hooks/useSearchSheet';
import { useDiscoverHref } from '@/hooks/useDiscoverHref';
import { formatSearchCityLabel, formatSearchCityPlaceholder } from '@/lib/copy/searchCityCopy';
import {
  classifyLocationSuggestionKind,
  resolveNeighborhoodSlug,
} from '@/lib/neighborhoods/clt-slugs';
import { emitSearchEvent } from '@/lib/telemetry/searchEvents';
import type { SearchDismissReason } from '@/lib/telemetry/searchEvents';
import type { Event } from '@/types/event';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SearchSheetProps {
  /** Whether the sheet is open. Mirrors the controlled state in `useSearchSheet`. */
  open: boolean;
  /**
   * Called when Radix requests an open/close state change (Escape,
   * backdrop click, programmatic close, etc.).
   */
  onOpenChange: (open: boolean) => void;
  /** Active city — used to compose the discover URL on result selection. */
  cityCode: CityCode;
  /**
   * City display name for human-facing copy (PL-09 / WEB-620). Threaded from
   * the city registry via `SearchSheetMount` — never derived from `cityCode`
   * inside this component. Omit / empty → city-free "Search…" (apex / no-city).
   */
  cityName?: string | null;
  /**
   * Optional pre-fetched events used as a client-side fallback for
   * suggestions. Defaults to an empty array — `SearchAutocomplete` will
   * fall back to the server-side suggestion API.
   */
  events?: Event[];
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** `data-testid` for the input — referenced by AppHeader auto-focus tests. */
export const SEARCH_SHEET_INPUT_TEST_ID = 'search-sheet-input';

/**
 * Backdrop styling (WEB-220 + WEB-620):
 *   - `bg-background/85` — translucent surface token (was hardcoded
 *     `rgba(15,20,31,0.85)` as a F-10 workaround when city `.dark` was inert).
 *     Post-WEB-612 the city surface keys `data-theme="dark"`, so
 *     `--color-background` resolves to `#0f141f` and this opacity match is
 *     the same navy without fighting the token system.
 *   - `backdrop-filter: blur(20px)` — heavy frosted glass
 *
 * Content panel keeps Dialog's `bg-background` (opaque) — that is the
 * surface AC for WEB-620; the overlay only dims what sits underneath.
 */
const OVERLAY_CLASSNAME = cn(
  'bg-background/85 backdrop-blur-[20px]',
  // WEB-611: SearchSheet is a modal over city/app chrome. Ride `--z-modal`
  // (100) so the overlay paints above the dock (`--z-dock`: 90). The dialog
  // atom still defaults to Tailwind `z-50`; this class wins via cn()/merge.
  'z-modal',
);

/**
 * Content styling — desktop slides down from the top, mobile occupies the
 * full viewport. Overrides the default Radix `DialogContent` centering
 * styles set on the atom (which we cannot remove without forking).
 */
const CONTENT_CLASSNAME = cn(
  // Match overlay on the modal rung (overrides dialog atom `z-50`).
  'z-modal',
  // Reset the centered-card defaults from the dialog atom.
  'top-0 left-0 translate-x-0 translate-y-0',
  // Mobile: full-viewport.
  'h-[100dvh] max-h-[100dvh] w-screen max-w-[100vw]',
  'rounded-none border-0 p-0 shadow-none',
  // Desktop (≥720px): top-anchored card with rounded corners, max width,
  // bounded height, sliding down from the top edge.
  'sm:top-4 sm:left-1/2 sm:-translate-x-1/2',
  'sm:h-auto sm:max-h-[80dvh] sm:w-[calc(100vw-2rem)] sm:max-w-2xl',
  'sm:rounded-xl sm:border sm:p-0 sm:shadow-2xl',
  // Slide-down animation override for desktop — Radix's default is a
  // zoom from center; replace it with a top-anchored slide.
  'sm:data-[state=open]:slide-in-from-top-4 sm:data-[state=open]:zoom-in-100',
  'sm:data-[state=closed]:slide-out-to-top-4 sm:data-[state=closed]:zoom-out-100',
  // Layout — vertical column for input + autocomplete results.
  'flex flex-col gap-0 overflow-hidden',
);

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

/**
 * Modal sheet hosting global search. See file-level docstring for behavior.
 */
export function SearchSheet({
  open,
  onOpenChange,
  cityCode,
  cityName,
  events = [],
}: SearchSheetProps): React.ReactElement {
  const router = useRouter();
  const buildDiscoverHref = useDiscoverHref(cityCode);
  const [query, setQuery] = React.useState<string>('');
  const [activeItemId, setActiveItemId] = React.useState<string | undefined>(undefined);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  // WEB-620 / PL-09 — one voice with the header pill + menu row.
  const searchLabel = formatSearchCityLabel(cityName);
  const searchPlaceholder = formatSearchCityPlaceholder(cityName);

  // FRE-829: Track the element that had keyboard focus immediately before
  // the sheet opened so we can restore focus to it on close. Radix's
  // default focus-restore depends on `<DialogTrigger>` being the opener,
  // but our sheet is opened via the controlled `open` prop wired through
  // `useSearchSheet()` — Radix never sees the trigger.
  //
  // CAPTURE TIMING (CR thread #3 on PR #3907): the ref is written inside
  // `onOpenAutoFocus` BEFORE we move focus to the input. A
  // `useEffect([open])` capture would race with Radix's focus-trap mount
  // and `document.activeElement` could already be the dialog content by
  // the time it runs. The `onOpenAutoFocus` event fires synchronously on
  // the open transition, before any focus changes, so the captured value
  // is always the real pre-open focus target (typically the AppHeader
  // search trigger).
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  // FRE-822 telemetry: read the trigger source (button / kbd / slash) that
  // the caller stamped on the shared store. Defaults to `'button'` so legacy
  // callers that haven't been migrated to `openSheet({ trigger })` still
  // emit a sensible `search_opened` event.
  const { openTrigger } = useSearchSheet();

  // Reason recorded by the next close cycle. Click handlers and Radix's
  // dismiss callbacks set this BEFORE the `open` prop flips to false. We
  // hold it in a ref (not state) because the SearchSheet has already
  // closed by the time the dismiss `useEffect` fires — a setState would
  // be batched into the already-mounted re-render and lost.
  //
  // Defaults to `'backdrop'` because that's the only Radix dismiss path
  // that doesn't fire its own callback (`onEscapeKeyDown` covers Escape;
  // `onInteractOutside` covers backdrop, but we still mark it explicitly
  // for safety).
  const dismissReasonRef = React.useRef<SearchDismissReason>('backdrop');

  // Reset transient state when the sheet closes — opening it again should
  // start with a clean slate, not show the prior query/active descendant.
  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveItemId(undefined);
    }
  }, [open]);

  // FRE-822: emit `search_opened` on every open transition, `search_dismissed`
  // on every close transition. Mirrors the Radix open/close lifecycle, so
  // both the user-initiated paths (Escape, backdrop, select) and any
  // programmatic open/close are covered.
  const wasOpenRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      emitSearchEvent({ type: 'search_opened', trigger: openTrigger ?? 'button' });
      wasOpenRef.current = true;
      // Reset the dismiss reason for the next close cycle. The default
      // `'backdrop'` covers programmatic close paths that don't go through
      // any of our annotated handlers.
      dismissReasonRef.current = 'backdrop';
      return;
    }
    if (!open && wasOpenRef.current) {
      emitSearchEvent({ type: 'search_dismissed', reason: dismissReasonRef.current });
      wasOpenRef.current = false;
    }
  }, [open, openTrigger]);

  /**
   * Handle a result selection from the autocomplete.
   *
   * `value` is the human-readable label/query; `type` is the suggestion
   * type (`artist`, `venue`, `event`, `recent`, `popular`, `location`).
   * Artist/venue/event/recent/popular all route to the per-city discover
   * surface with `q=<value>`. Future iterations may switch on `type` (e.g.
   * route directly to a venue page for `type === 'venue'`), but that's out
   * of scope for WEB-220 — the issue scopes navigation to `router.push()`
   * and close.
   *
   * `type === 'location'` (WEB-965) is special-cased: a city suggestion
   * (e.g. "Charlotte, NC") matches no `Location.neighborhood` row, so
   * routing it through `q=` yields an empty Discover list. Instead:
   *   - A known CLT neighborhood (per `classifyLocationSuggestionKind` /
   *     `resolveNeighborhoodSlug`, sharing the same precedence rules as
   *     `useDiscoverSearch`) routes to `?neighborhood=<slug>`.
   *   - Everything else — including a city, and defensively any location
   *     row that doesn't resolve to a known neighborhood even when the
   *     backend's `locationKind` is stale/absent — routes to the
   *     unfiltered city discover list with no `q` and no `neighborhood`.
   *
   * For FRE-822 telemetry, the caller passes one of:
   *   - `'select'` when a real autocomplete suggestion is chosen — paired
   *     with a separate `search_result_clicked` / `search_recent_used`
   *     event fired by `SearchAutocomplete`.
   *   - `'navigation'` when the user submits free-text via Enter (no
   *     suggestion was active), so the only telemetry beat is the
   *     `search_dismissed` event with `reason: 'navigation'`.
   */
  const handleSelect = React.useCallback(
    (
      value: string,
      type: string,
      dismissReason: SearchDismissReason = 'select',
      locationKind?: 'city' | 'neighborhood',
    ) => {
      dismissReasonRef.current = dismissReason;
      const params = new URLSearchParams();
      const trimmed = value.trim();

      if (type === 'location') {
        // WEB-965: classify defensively — a stale/absent backend
        // `locationKind` must not fall back to treating the row as a
        // neighborhood filter (that zeroes the result list).
        const kind = classifyLocationSuggestionKind(trimmed, locationKind);
        const slug = kind === 'neighborhood' ? resolveNeighborhoodSlug(trimmed) : null;
        if (slug) {
          params.set('neighborhood', slug);
        }
        // A city (or an unresolved location row) writes no `q` and no
        // `neighborhood` — the unfiltered city discover list.
      } else if (trimmed.length > 0) {
        params.set('q', trimmed);
      }

      const search = params.toString();
      // WEB-1231 / R25: a signed-in viewer lands on `app.<root>/<city>/discover`;
      // only an anonymous city-host visitor stays on `/discover`.
      const target = buildDiscoverHref(search);
      router.push(target);
      onOpenChange(false);
    },
    [buildDiscoverHref, onOpenChange, router],
  );

  /**
   * Submit-on-Enter when no autocomplete row is active. The autocomplete
   * organism handles Enter when an item is highlighted; this fallback
   * fires the free-text query through the same `handleSelect` path so the
   * user can search even if the suggestion list is empty.
   *
   * Marked with dismiss reason `'navigation'` so the FRE-822
   * `search_dismissed` event can distinguish suggestion-select from a
   * free-text submit.
   */
  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      if (activeItemId) return; // autocomplete will handle it
      const trimmed = query.trim();
      if (trimmed.length === 0) return;
      event.preventDefault();
      handleSelect(trimmed, 'query', 'navigation');
    },
    [activeItemId, handleSelect, query],
  );

  // Autocomplete's `onSelect` carries no dismissReason — wrap it so a
  // suggestion-click always records `'select'` rather than the fallback.
  const handleAutocompleteSelect = React.useCallback(
    (value: string, type: string, locationKind?: 'city' | 'neighborhood') => {
      handleSelect(value, type, 'select', locationKind);
    },
    [handleSelect],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="search-sheet"
        className={CONTENT_CLASSNAME}
        overlayClassName={OVERLAY_CLASSNAME}
        // Radix sets aria-modal=true automatically; we do not auto-focus
        // the close button — instead the input below claims focus on open.
        onOpenAutoFocus={(event) => {
          // FRE-829: capture the currently-focused element BEFORE we move
          // focus to the input. This handler fires synchronously on the
          // open transition, before Radix's focus trap claims focus, so
          // `document.activeElement` is still the trigger that opened the
          // sheet. Stored in `previouslyFocusedRef` and replayed in
          // `onCloseAutoFocus` to land focus back on the trigger.
          const active = document.activeElement;
          if (active instanceof HTMLElement && active !== document.body) {
            previouslyFocusedRef.current = active;
          }

          // Prevent Radix from focusing the first focusable child (the
          // close X) and let our input claim focus instead.
          event.preventDefault();
          inputRef.current?.focus();
        }}
        // FRE-829: explicit focus restore — preempt Radix's default
        // restoration (which falls back to `document.body` when the dialog
        // wasn't opened via `<DialogTrigger>`) and put focus back on the
        // element that opened the sheet.
        onCloseAutoFocus={(event) => {
          const target = previouslyFocusedRef.current;
          if (target && document.contains(target)) {
            event.preventDefault();
            target.focus();
          }
        }}
        // FRE-822: stamp the dismiss reason BEFORE `open` flips so the
        // close-side telemetry effect sees the correct value.
        onEscapeKeyDown={() => {
          dismissReasonRef.current = 'escape';
        }}
        onInteractOutside={() => {
          dismissReasonRef.current = 'backdrop';
        }}
      >
        {/* Visually-hidden title — required by Radix for assistive tech. */}
        <DialogTitle className="sr-only">{searchLabel}</DialogTitle>

        {/* Search input row */}
        <div className="flex items-center gap-3 border-b px-4 py-3 sm:px-5 sm:py-4">
          <SearchIcon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          {/* DS 1.9.0 `.m-search__input`: a bare input inside a search row that
              owns the border — not the boxed `.a-input` field. 15px on fine
              pointers, 16px on coarse (iOS zooms a focused field under 16). */}
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchLabel}
            aria-autocomplete="list"
            aria-controls="search-autocomplete-listbox"
            aria-activedescendant={activeItemId}
            data-testid={SEARCH_SHEET_INPUT_TEST_ID}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-body text-fg-1 outline-none placeholder:text-fg-3 pointer-coarse:text-base"
          />
        </div>

        {/* Autocomplete body — relative anchors the absolute desktop dropdown
            inside this column rather than at viewport-top. */}
        <div className="relative flex-1 overflow-y-auto">
          <SearchAutocomplete
            isOpen={open}
            query={query}
            events={events}
            cityCode={cityCode}
            onSelect={handleAutocompleteSelect}
            onDismiss={() => {
              // ESC inside the autocomplete keyboard handler bypasses
              // Radix's `onEscapeKeyDown`, so stamp the reason manually
              // before Radix's `onOpenChange(false)` fires.
              dismissReasonRef.current = 'escape';
              onOpenChange(false);
            }}
            onActiveItemIdChange={setActiveItemId}
            // Override the autocomplete's default fixed/desktop-absolute
            // positioning — inside the sheet it should fill the body.
            className="!relative !inset-auto !top-auto !bottom-auto !z-auto !rounded-none !border-0 !shadow-none !backdrop-blur-none !bg-transparent"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
